import { AppDispatcher } from '@models/common.models';
import { requestAction } from '@common/components/common.actions';
import { invoiceService } from '@services/InvoiceService';
import {
  InvoicePaymentItemViewModel,
  InvoiceLineCreateViewModel,
  InvoicePaymentHistoryViewModel
} from '@models/InvoicePaymentItem';
import { Action } from 'redux';
import { AppState } from 'src';
import { SwoCviViewModel } from '@models/AuthorizationSwoCvi';
import { actions } from 'react-redux-form';
import FormNames from '@constants/FormNames.enum';
import { InvoiceLineForm } from './_components/invoice-create/_components/swo-invoice-line/swo-invoice-line.component';
import { authorizationService } from '../_services/AuthorizationService';
import { AuthorizationLine } from '@models/AuthorizationLine';

export enum InvoicePaymentLineActionType {
  SetAllPaymentItems = 'SetAllPaymentItems',
  SetInvoicePaymentItemsTableLoading = 'SetInvoicePaymentItemsTableLoading',
  DeleteInvoicePaymentListItem = 'DeleteInvoicePaymentListItem',

  SetSelectedCvi = 'InvoiceLineSetSelectedCvi',
  SetLineItemSaveLoading = 'SetInvoiceLineItemSaveLoading',

  SetAuthorizationLines = 'SetInvoiceAuthorizationLines',
  SetAuthorizationLinesLoading = 'SetInvoiceAuthorizationLinesLoading',

  SetPaymentHistory = 'SetPaymentHistory',
  SetPaymentHistoryLoading = 'SetPaymentHistoryLoading'
}

export interface SetAllPaymentItemsAction extends Action {
  readonly type: InvoicePaymentLineActionType.SetAllPaymentItems;
  readonly items: Array<InvoicePaymentItemViewModel>;
}

export interface SetPaymentItemsLoadingAction extends Action {
  readonly type: InvoicePaymentLineActionType.SetInvoicePaymentItemsTableLoading;
  readonly loading: boolean;
}

export interface DeleteInvoicePaymentListItemAction extends Action {
  readonly type: InvoicePaymentLineActionType.DeleteInvoicePaymentListItem;
  readonly lineNumber: number;
  readonly invoiceId: string;
}

export interface SetSelectedInvoiceLineCvi extends Action {
  readonly type: InvoicePaymentLineActionType.SetSelectedCvi;
  readonly cvi?: SwoCviViewModel;
}

export interface SetInvoiceLineSaveLoadingAction extends Action {
  readonly type: InvoicePaymentLineActionType.SetLineItemSaveLoading;
  readonly loading: boolean;
}

export interface SetInvoiceAuthorizationLinesAction extends Action {
  readonly type: InvoicePaymentLineActionType.SetAuthorizationLines;
  readonly lines: AuthorizationLine[];
}

export interface SetInvoiceAuthorizationLinesLoadingAction extends Action {
  readonly type: InvoicePaymentLineActionType.SetAuthorizationLinesLoading;
  readonly loading: boolean;
}

export function getAuthoLines(swoId: number, detailNr?: number, payeeType?: number, payeeVendorId?: number) {
  return async (dispatch: AppDispatcher) => {
    if (swoId === 0) {
      return;
    }
    dispatch(
      requestAction(
        () => authorizationService.getAuthorizationLines(swoId, detailNr, payeeType, payeeVendorId),
        setInvoiceAuthorizationLines,
        {
          loadingAction: setInvoiceAuthoLinesLoading
        }
      )
    );
  };
}

export function loadAuthoLine(line: AuthorizationLine) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    if (!line) {
      return;
    }
    const am = line.authorizedUnitCount * line.authorizedUnitAmount;
    const newLine: InvoiceLineForm = {
      cviDetailNumber: '' + line.detailNumber,
      glCode: '' + line.glCode.id,
      approved: am,
      submitted: am,
      lineType: '' + line.lineType.id,
      authoLineNumber: line.lineNumber
    };
    dispatch(actions.change(FormNames.InvoiceLineCreate, newLine));
    const cvi = getState().invoicing.invoicableCvis.find(x => x.swoDetailNumber === line.detailNumber);
    if (!cvi) {
      return;
    }
    return dispatch(setInvoiceLineCvi(cvi));
  };
}

export function createInvoiceLineFromAuthoLine(invoiceId: number, swoId: number, line: AuthorizationLine) {
  return async (dispatch: AppDispatcher) => {
    const am = line.authorizedUnitCount * line.authorizedUnitAmount;
    const newLine: InvoiceLineCreateViewModel = {
      invoiceId: invoiceId,
      swoId: swoId,
      detailNumber: line.detailNumber,
      glCode: line.glCode.id,
      approved: am,
      submitted: am,
      lineItemType: line.lineType.id,
      authoLineNumber: line.lineNumber
    };
    return dispatch(createLineItem(invoiceId, newLine));
  };
}

export function setInvoiceAuthoLinesLoading(loading: boolean) {
  return {
    type: InvoicePaymentLineActionType.SetAuthorizationLinesLoading,
    loading: loading
  };
}

export function setInvoiceAuthorizationLines(authoLines: AuthorizationLine[]): SetInvoiceAuthorizationLinesAction {
  return {
    type: InvoicePaymentLineActionType.SetAuthorizationLines,
    lines: authoLines
  };
}

export function loadLineItemForEdit(invoiceItem: InvoicePaymentItemViewModel) {
  return (dispatch: AppDispatcher, getState: () => AppState) => {
    let newItem: InvoiceLineForm = {
      cviDetailNumber: '' + invoiceItem.cvi.swoDetailNumber,
      glCode: '' + invoiceItem.glCode.glMapCd,
      lineType: '' + invoiceItem.lineItemType.id,
      submitted: invoiceItem.submitted,
      approved: invoiceItem.approved
    };
    if (invoiceItem.authoLineNumber) {
      newItem = {
        ...newItem,
        authoLineNumber: invoiceItem.authoLineNumber
      };
    }
    dispatch(actions.change(FormNames.InvoiceLineCreate, newItem));
    dispatch(setInvoiceLineCvi(invoiceItem.cvi));
  };
}

export function getPaymentListItems(invoiceId: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    if (getState().invoicing.paymentLines.isTableLoading) {
      return;
    }
    dispatch(
      requestAction(() => invoiceService.getInvoicePaymentItems('' + invoiceId), setInvoicePaymentItems, {
        hasToast: false,
        loadingAction: setPaymentItemListLoading
      })
    );
  };
}

export function deletePaymentListItem(invoiceId: string, itemLineNumber: number) {
  return async (dispatch: AppDispatcher) => {
    return dispatch(
      requestAction(
        () => invoiceService.deleteInvoicePaymentItem(invoiceId, itemLineNumber),
        () => {
          return {
            type: InvoicePaymentLineActionType.DeleteInvoicePaymentListItem,
            invoiceId: invoiceId,
            lineNumber: itemLineNumber
          };
        },
        {
          toastSuccessMessage: 'Deleted Successfully.',
          overrideFailMessage: 'Unable to delete payment line item.',
          hasToast: true,
          supressErrors: false
        }
      )
    );
  };
}

export function setInvoicePaymentItems(items: Array<InvoicePaymentItemViewModel>) {
  return {
    type: InvoicePaymentLineActionType.SetAllPaymentItems,
    items: items
  };
}

export function setPaymentItemListLoading(loading: boolean) {
  return {
    type: InvoicePaymentLineActionType.SetInvoicePaymentItemsTableLoading,
    loading: loading
  };
}

export function setInvoiceLineCvi(cvi?: SwoCviViewModel): SetSelectedInvoiceLineCvi {
  return {
    type: InvoicePaymentLineActionType.SetSelectedCvi,
    cvi: cvi
  };
}

export function lineItemSavingAction(loading: boolean): SetInvoiceLineSaveLoadingAction {
  return {
    type: InvoicePaymentLineActionType.SetLineItemSaveLoading,
    loading: loading
  };
}

export function editLineItem(invoiceId: number, lineNumber: number, line: InvoiceLineCreateViewModel) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    const lineState = getState().invoicing.paymentLines;
    if (lineState.isSaving) {
      return Promise.reject('');
    }
    return dispatch(
      requestAction(
        () => invoiceService.updateLineItem(invoiceId, lineNumber, line),
        () => {
          return { type: '' };
        },
        { hasToast: true, toastSuccessMessage: 'Line item updated successfully.', loadingAction: lineItemSavingAction }
      )
    );
  };
}

export function createLineItem(invoiceId: number, line: InvoiceLineCreateViewModel) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    const lineState = getState().invoicing.paymentLines;
    if (lineState.isSaving) {
      return Promise.reject('');
    }
    return dispatch(
      requestAction(
        () => invoiceService.createLineItem(invoiceId, line),
        () => {
          return { type: '' };
        },
        { hasToast: true, toastSuccessMessage: 'Line item created successfully.', loadingAction: lineItemSavingAction }
      )
    );
  };
}

export function generateAllInvoiceLinesForSwo(invoiceId?: number) {
  return async (dispatch: AppDispatcher) => {
    if (invoiceId === undefined) {
      return;
    }
    return dispatch(
      requestAction(
        () => invoiceService.generateAllInvoiceLinesForSwo(invoiceId),
        () => {
          return { type: '' };
        },
        {
          hasToast: true,
          toastSuccessMessage: 'All Invoice Lines successfully built from available authorizations.'
        }
      )
    );
  };
}

export function getPaymentHistory(invoiceId: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    if (getState().invoicing.paymentLines.historyLoading) {
      return;
    }
    dispatch(
      requestAction(() => invoiceService.getInvoicePaymentHistory(invoiceId), setPaymentHistory, {
        hasToast: false,
        loadingAction: setPaymentHistoryLoading
      })
    );
  };
}
export interface SetPaymentHistoryAction extends Action {
  readonly type: InvoicePaymentLineActionType.SetPaymentHistory;
  readonly items: Array<InvoicePaymentHistoryViewModel>;
}

export function setPaymentHistory(items: Array<InvoicePaymentHistoryViewModel>) {
  return {
    type: InvoicePaymentLineActionType.SetPaymentHistory,
    items: items
  };
}

export interface SetPaymentHistoryLoadingAction extends Action {
  readonly type: InvoicePaymentLineActionType.SetPaymentHistoryLoading;
  readonly loading: boolean;
}

export function setPaymentHistoryLoading(loading: boolean) {
  return {
    type: InvoicePaymentLineActionType.SetPaymentHistoryLoading,
    loading: loading
  };
}



// WEBPACK FOOTER //
// ./src/invoicing/invoice-line.actions.tsx